import React, { createContext, useState, useEffect } from 'react';

/* ══════════════════════════════════════════
   Hell / Dunkel
   ──────────────────────────────────────────
   Zwei Zustände, nicht drei. Beim ersten Besuch gilt die Einstellung des
   Geräts; ab dem ersten Klick gilt die eigene Wahl und wird behalten.

   Genau so macht es die Website (vendor/dpsg/js/theme-switcher.js): sie
   liest `prefers-color-scheme` EINMAL beim Laden und folgt danach nicht mehr
   live. Ein „System"-Modus als dritte Wahl gab es hier vorher; er war eine
   Erfindung des Dashboards und ist wieder weg.

   Werte und Attribut heißen wie dort: 'hell' | 'dunkel', und hell ist der
   :root-Default — das Attribut wird dann entfernt, nicht auf "hell" gesetzt.

   Gespeichert wird in localStorage, nicht im Cookie: die Einstellung muss
   nur auf diesem Gerät überdauern, und dafür ist localStorage das kleinere
   Mittel — es verlässt den Browser nie und wird bei keiner Anfrage
   mitgeschickt.
   ══════════════════════════════════════════ */

export const ThemeContext = createContext({ theme: 'hell', toggleTheme: () => {} });

const STORAGE_KEY = 'dpsg-theme';

const geraetMagDunkel = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;

/* Frühere Fassungen speicherten 'light'/'dark'. Wer damals umgestellt hat,
   soll seine Wahl behalten. */
function migrate(v) {
  if (v === 'hell' || v === 'dunkel') return v;
  if (v === 'light') return 'hell';
  if (v === 'dark') return 'dunkel';
  return null;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const gespeichert = migrate(localStorage.getItem(STORAGE_KEY));
      if (gespeichert) return gespeichert;
    } catch {
      /* ignore */
    }
    return geraetMagDunkel() ? 'dunkel' : 'hell';
  });

  useEffect(() => {
    const el = document.documentElement;
    if (theme === 'dunkel') el.setAttribute('data-theme', 'dunkel');
    else el.removeAttribute('data-theme');
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dunkel' ? 'hell' : 'dunkel';
    setTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}
